<script setup>
import { computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useContactProfileIdentityLock } from 'dashboard/composables/useContactProfileIdentityLock';
import { dynamicTime } from 'shared/helpers/timeHelper';

import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ContactLabels from 'dashboard/components-next/Contacts/ContactLabels/ContactLabels.vue';
import ContactsForm from 'dashboard/components-next/Contacts/ContactsForm/ContactsForm.vue';
import ConfirmContactDeleteDialog from 'dashboard/components-next/Contacts/ContactsForm/ConfirmContactDeleteDialog.vue';
import Policy from 'dashboard/components/policy.vue';
import { CONTACT_DELETE_PERMISSIONS } from 'dashboard/constants/permissions';

const props = defineProps({
  selectedContact: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['goToContactsList']);

const { t } = useI18n();
const store = useStore();
const { isContactIdentityLockedForCurrentUser, canEditContactProfile } =
  useContactProfileIdentityLock();

const confirmDeleteContactDialogRef = ref(null);

const avatarFile = ref(null);
const avatarUrl = ref('');

const contactsFormRef = ref(null);

const uiFlags = useMapGetter('contacts/getUIFlags');
const isUpdating = computed(() => uiFlags.value.isUpdating);

const isFormInvalid = computed(() => contactsFormRef.value?.isFormInvalid);

const contactData = ref({});

const getInitialContactData = () => {
  if (!props.selectedContact) return {};
  return { ...props.selectedContact };
};

onMounted(() => {
  Object.assign(contactData.value, getInitialContactData());
});

const createdAt = computed(() => {
  return contactData.value?.createdAt
    ? dynamicTime(contactData.value.createdAt)
    : '';
});

const lastActivityAt = computed(() => {
  return contactData.value?.lastActivityAt
    ? dynamicTime(contactData.value.lastActivityAt)
    : '';
});

const avatarSrc = computed(() => {
  return avatarUrl.value ? avatarUrl.value : contactData.value?.thumbnail;
});

// WhatsApp identity metadata (BSUID / username). The new contacts store camelizes keys, so we read
// both cases defensively. These are display-only; source of truth for BSUID is contact_inboxes.
const whatsappAttributes = computed(() => {
  return (
    props.selectedContact?.additionalAttributes ||
    props.selectedContact?.additional_attributes ||
    {}
  );
});

const whatsappUsername = computed(() => {
  const attrs = whatsappAttributes.value;
  return attrs.whatsappUsername || attrs.whatsapp_username || '';
});

const instagramUsername = computed(() => {
  const attrs = whatsappAttributes.value;
  const socialProfiles = attrs.socialProfiles || attrs.social_profiles || {};
  return (
    attrs.socialInstagramUserName ||
    attrs.social_instagram_user_name ||
    socialProfiles.instagram ||
    ''
  );
});

const whatsappBsuid = computed(() => {
  const attrs = whatsappAttributes.value;
  return attrs.whatsappBsuid || attrs.whatsapp_bsuid || '';
});

const handleFormUpdate = updatedData => {
  Object.assign(contactData.value, updatedData);
};

const updateContact = async () => {
  try {
    const { customAttributes, ...basicContactData } = contactData.value;
    let payload = { ...basicContactData };
    if (isContactIdentityLockedForCurrentUser.value) {
      payload = { ...payload };
      delete payload.name;
      delete payload.phoneNumber;
    }
    await store.dispatch('contacts/update', payload);
    await store.dispatch(
      'contacts/fetchContactableInbox',
      props.selectedContact.id
    );
    useAlert(t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.ERROR_MESSAGE'));
  }
};

const openConfirmDeleteContactDialog = () => {
  confirmDeleteContactDialogRef.value?.dialogRef.open();
};

const handleAvatarUpload = async ({ file, url }) => {
  avatarFile.value = file;
  avatarUrl.value = url;

  try {
    await store.dispatch('contacts/update', {
      ...contactsFormRef.value?.state,
      avatar: file,
      isFormData: true,
    });
    useAlert(t('CONTACTS_LAYOUT.DETAILS.AVATAR.UPLOAD.SUCCESS_MESSAGE'));
  } catch {
    useAlert(t('CONTACTS_LAYOUT.DETAILS.AVATAR.UPLOAD.ERROR_MESSAGE'));
  }
};

const handleAvatarDelete = async () => {
  try {
    if (props.selectedContact && props.selectedContact.id) {
      await store.dispatch('contacts/deleteAvatar', props.selectedContact.id);
      useAlert(t('CONTACTS_LAYOUT.DETAILS.AVATAR.DELETE.SUCCESS_MESSAGE'));
    }
    avatarFile.value = null;
    avatarUrl.value = '';
    contactData.value.thumbnail = null;
  } catch (error) {
    useAlert(
      error.message
        ? error.message
        : t('CONTACTS_LAYOUT.DETAILS.AVATAR.DELETE.ERROR_MESSAGE')
    );
  }
};
</script>

<template>
  <div class="flex flex-col items-start gap-8 pb-6">
    <div class="flex flex-col items-start gap-3">
      <Avatar
        :src="avatarSrc || ''"
        :name="selectedContact?.name || ''"
        :size="72"
        :allow-upload="canEditContactProfile"
        @upload="handleAvatarUpload"
        @delete="handleAvatarDelete"
      />
      <div class="flex flex-col gap-1">
        <h3 class="text-base font-medium text-n-slate-12">
          {{ selectedContact?.name }}
        </h3>
        <div class="flex flex-col gap-1.5">
          <span
            v-if="selectedContact?.identifier"
            class="inline-flex items-center gap-1 text-sm text-n-slate-11"
          >
            <span class="i-ph-user-gear text-n-slate-10 size-4" />
            {{ selectedContact?.identifier }}
          </span>
          <span class="inline-flex items-center gap-1 text-sm text-n-slate-11">
            <span
              v-if="selectedContact?.identifier"
              class="i-ph-activity text-n-slate-10 size-4"
            />
            {{ $t('CONTACTS_LAYOUT.DETAILS.CREATED_AT', { date: createdAt }) }}
            •
            {{
              $t('CONTACTS_LAYOUT.DETAILS.LAST_ACTIVITY', {
                date: lastActivityAt,
              })
            }}
          </span>
        </div>
      </div>
      <ContactLabels :contact-id="selectedContact?.id" />
      <div
        v-if="whatsappUsername || whatsappBsuid || instagramUsername"
        class="flex flex-col gap-1.5"
      >
        <span
          v-if="whatsappUsername"
          class="inline-flex items-center gap-1 text-sm text-n-slate-11"
        >
          <span class="i-ph-chat-circle-text text-n-slate-10 size-4" />
          {{
            t('CONTACTS_LAYOUT.DETAILS.WHATSAPP_USERNAME', {
              value: whatsappUsername,
            })
          }}
        </span>
        <span
          v-if="instagramUsername"
          class="inline-flex items-center gap-1 text-sm text-n-slate-11"
        >
          <span class="i-ri-instagram-line text-n-slate-10 size-4" />
          {{
            t('CONTACTS_LAYOUT.DETAILS.INSTAGRAM_USERNAME', {
              value: String(instagramUsername).replace(/^@/, ''),
            })
          }}
        </span>
        <span
          v-if="whatsappBsuid"
          class="inline-flex items-center gap-1 text-sm text-n-slate-11"
        >
          <span class="i-ph-identification-card text-n-slate-10 size-4" />
          {{
            t('CONTACTS_LAYOUT.DETAILS.WHATSAPP_BSUID', {
              value: whatsappBsuid,
            })
          }}
        </span>
      </div>
    </div>
    <div v-if="canEditContactProfile" class="flex flex-col items-start gap-6">
      <p
        v-if="isContactIdentityLockedForCurrentUser"
        class="text-sm text-n-slate-11"
      >
        {{
          t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.IDENTITY_READ_ONLY_NOTICE')
        }}
      </p>
      <ContactsForm
        ref="contactsFormRef"
        :contact-data="contactData"
        :lock-identity-fields="isContactIdentityLockedForCurrentUser"
        is-details-view
        @update="handleFormUpdate"
      />
      <Button
        :label="t('CONTACTS_LAYOUT.CARD.EDIT_DETAILS_FORM.UPDATE_BUTTON')"
        size="sm"
        :is-loading="isUpdating"
        :disabled="isUpdating || isFormInvalid"
        @click="updateContact"
      />
    </div>
    <Policy :permissions="['administrator', CONTACT_DELETE_PERMISSIONS]">
      <div
        class="flex flex-col items-start w-full gap-4 pt-6 border-t border-n-strong"
      >
        <div class="flex flex-col gap-2">
          <h6 class="text-base font-medium text-n-slate-12">
            {{ t('CONTACTS_LAYOUT.DETAILS.DELETE_CONTACT') }}
          </h6>
          <span class="text-sm text-n-slate-11">
            {{ t('CONTACTS_LAYOUT.DETAILS.DELETE_CONTACT_DESCRIPTION') }}
          </span>
        </div>
        <Button
          :label="t('CONTACTS_LAYOUT.DETAILS.DELETE_CONTACT')"
          color="ruby"
          @click="openConfirmDeleteContactDialog"
        />
      </div>
      <ConfirmContactDeleteDialog
        ref="confirmDeleteContactDialogRef"
        :selected-contact="selectedContact"
        @go-to-contacts-list="emit('goToContactsList')"
      />
    </Policy>
  </div>
</template>
